import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { User, LogOut, Share2, Settings, Puzzle } from "lucide-react";
import { Smartphone } from "lucide-react";
import logo from "@/assets/logo-optimized.webp";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import LanguageSwitcher from "./LanguageSwitcher";
import { isAndroidNative } from "@/lib/quickfillBridge";

export function AppHeader() {
  const navigate = useNavigate();
  const { signOut, user } = useAuth();
  const { t } = useTranslation();

  const isElectron =
    typeof window !== "undefined" &&
    (window.electronAPI !== undefined || window.navigator.userAgent.includes("Electron"));

  const showQuickFill = isAndroidNative();

  // Fetch first asset list for home link
  const { data: firstListId } = useQuery({
    queryKey: ['first-asset-list', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data } = await supabase
        .from('asset_lists')
        .select('id')
        .or(`user_id.eq.${user.id},admin_user_ids.cs.{${user.id}},editor_user_ids.cs.{${user.id}},viewer_user_ids.cs.{${user.id}}`)
        .order('created_at', { ascending: true })
        .limit(1)
        .single();
      return data?.id || null;
    },
    enabled: !!user?.id,
  });

  const homeLink = firstListId ? `/app/list/${firstListId}` : '/app';

  return (
    <header className="border-b border-border bg-card pt-[env(safe-area-inset-top)]">
      <div className="flex items-center justify-between px-6 py-3">
        <div className="flex items-center gap-3">
          {user && <SidebarTrigger />}
          <Link to={homeLink} className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <img src={logo} alt="TestAssets" className="h-8 w-8 object-contain" />
            <span className="text-xl font-semibold">TestAssets</span>
          </Link>
        </div>
        
        <div className="flex items-center gap-3">
          {isElectron && (
            <Button variant="ghost" size="sm" onClick={() => navigate("/desktop-settings")} title="Desktop Settings">
              <Settings className="h-5 w-5" />
              Desktop Settings
            </Button>
          )}
          <LanguageSwitcher />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="rounded-full">
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-primary text-primary-foreground">
                    A
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 rounded-lg">
              <DropdownMenuItem disabled className="text-muted-foreground text-xs">
                {user?.email}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/app/share")}>
                <Share2 className="h-4 w-4 mr-2" />
                {t("appHeader.share", "Share")}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/applications")}>
                <Puzzle className="h-4 w-4 mr-2" />
                {t("appHeader.applications", "Applications")}
              </DropdownMenuItem>
              {showQuickFill && (
                <DropdownMenuItem onClick={() => navigate("/setup-quickfill")}>
                  <Smartphone className="h-4 w-4 mr-2" />
                  {t("appHeader.setupQuickFill", "Set up Quick Fill")}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => navigate("/app/settings")}>
                <User className="h-4 w-4 mr-2" />
                {t("appHeader.settings", "Settings")}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={signOut}>
                <LogOut className="h-4 w-4 mr-2" />
                {t("appHeader.logout", "Logout")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
